/**
 * Wspólny dropdown systemu projektowego.
 *
 * Wyrósł z `app/features/company-selection/Dropdown` (wybór firmy w pasku górnym)
 * i dostał tryb `multiple`, żeby zastąpić rodzinę legacy dropdownów z panelu admina
 * (`multiDropdown`, `groupMultiDropdown`, `wpf*Selector`) - klasowych, opartych
 * na shimie bootstrapowym i `componentWillReceiveProps`.
 *
 * Wartości są tekstami: liczby konwertuj `String()` / `Number()` na granicy.
 */
import * as React from 'react'
import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import clx from 'classnames'

import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { CheckIcon } from '@/app/features/icons/Check'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { FaIcon } from '@/lib/fa'
import { faClose } from '@fortawesome/free-solid-svg-icons'

export type Option = {
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
  /** Pozycja widoczna, ale nie do wyboru (np. serwer już zajęty przez inną firmę). */
  disabled?: boolean
  /** Trafia na `title` pozycji - podpowiedź, dlaczego jest zablokowana. */
  title?: string
}

type Props = {
  placeholder?: string
  icon?: React.ElementType
  options: Array<Option>
  className?: string
  /** Pojedynczy wybór; przy `multiple` - tablica zaznaczonych wartości. */
  value?: string | null | string[]
  onChange?: (value: any) => void
  /**
   * Wybór wielokrotny: menu zostaje otwarte, pozycje mają znaczniki, a wyzwalacz
   * pokazuje `N z M`. Zastępuje legacy `MultiDropdown` z panelu admina.
   */
  multiple?: boolean
  /** Widoczne przy `multiple` - pozwala zaznaczyć/odznaczyć wszystko naraz. */
  showSelectAll?: boolean
  /** Przy `multiple` wyzwalacz pokazuje nazwy zaznaczonych zamiast `N z M`. */
  showSelectedNames?: boolean
  /**
   * Renderuje listę w portalu do `body`, pozycjonowaną `fixed`. Dla dropdownów
   * siedzących w kontenerze z `overflow: auto` (ciało modala panelu admina) -
   * inaczej rozwinięta lista jest przycinana do wysokości kontenera.
   */
  portalMenu?: boolean
  /**
   * Dokładane do klas wyzwalacza - np. `tw-border-0` dla dropdownu siedzącego
   * w komórce tabeli, gdzie ramka i tak jest rysowana przez wiersz.
   */
  triggerClassName?: string
  name?: string
  disabled?: boolean
  hideArrow?: boolean
  collapsed?: boolean
  searchPlaceholder?: string
  // Smaller trigger + narrower menu, for dense filter toolbars (default: full size).
  compact?: boolean
  // Pole wyszukiwania w menu; domyślnie ukryte tylko przy `compact`.
  searchable?: boolean
}

export const Dropdown = ({
  placeholder = 'Wybierz...',
  icon: Icon = null,
  options,
  className,
  value,
  disabled,
  hideArrow = false,
  name,
  collapsed,
  onChange,
  searchPlaceholder = 'Szukaj...',
  compact = false,
  searchable,
  multiple = false,
  showSelectedNames = false,
  showSelectAll = false,
  portalMenu = false,
  triggerClassName,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [query, setQuery] = useState('')

  const selectedValues = React.useMemo<string[]>(
    () => (multiple ? ((value as string[]) ?? []) : []),
    [multiple, value],
  )
  const isSelected = (option: Option) =>
    multiple ? selectedValues.includes(option.value) : value === option.value

  const selected = multiple
    ? null
    : options.find((o) => o.value === value) || null

  // Wyzwalacz pokazuje `content`, jeśli pozycja je ma (np. próbka koloru statusu
  // komunikatu) - inaczej wyświetlałby surową wartość typu `#459A18`.
  const triggerLabel: React.ReactNode = multiple
    ? selectedValues.length > 0
      ? (showSelectedNames &&
          options
            .filter((o) => selectedValues.includes(o.value))
            .map((o) => o.label)
            .join(', ')) ||
        `${selectedValues.length} z ${options.length}`
      : placeholder
    : selected
      ? (selected.content ?? selected.label)
      : placeholder
  const hasSelection = multiple ? selectedValues.length > 0 : !!selected

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const [menuRect, setMenuRect] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const ignoreRefs = React.useMemo(() => [menuRef], [])

  useClickOutside({
    ref: dropdownRef,
    ignoreRefs,
    handler: () => {
      setOpen(false)
      setQuery('')
      setFocusedIndex(-1)
    },
  })

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = q
      ? options.filter(
          (o) =>
            o.label.toString().toLowerCase().includes(q) ||
            o.value.toString().toLowerCase().includes(q),
        )
      : options

    if (multiple) return base
    return [...base].sort((a, b) =>
      selected?.value === a.value ? -1 : selected?.value === b.value ? 1 : 0,
    )
  }, [options, query, selected?.value, multiple])

  const toggle = () => {
    if (!disabled) setOpen((v) => !v)
  }

  const select = (option: Option) => {
    // Zablokowaną pozycję da się tylko odznaczyć, nigdy zaznaczyć - tak działały
    // stare selektory WPF-owe dla serwerów/baz zajętych przez inną firmę.
    if (option.disabled && !(multiple && selectedValues.includes(option.value))) {
      return
    }
    if (multiple) {
      // Menu zostaje otwarte - przy wielokrotnym wyborze zamykanie po każdym
      // kliknięciu było najczęstszą pretensją do starego `MultiDropdown`.
      const next = selectedValues.includes(option.value)
        ? selectedValues.filter((v) => v !== option.value)
        : [...selectedValues, option.value]
      onChange?.(next)
      return
    }
    setOpen(false)
    setQuery('')
    setFocusedIndex(-1)
    onChange?.(option.value)
  }

  const toggleAll = () => {
    const allValues = filteredOptions.map((o) => o.value)
    const allSelected = allValues.every((v) => selectedValues.includes(v))
    onChange?.(
      allSelected
        ? selectedValues.filter((v) => !allValues.includes(v))
        : Array.from(new Set([...selectedValues, ...allValues])),
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (
        e.key === 'Enter' ||
        e.key === ' ' ||
        (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key))
      ) {
        e.preventDefault()
        setOpen(true)
        if (e.key.length === 1) setQuery(e.key)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filteredOptions[focusedIndex]
      if (option) select(option)
    } else if (e.key === 'Escape') {
      setOpen(false)
      setQuery('')
      setFocusedIndex(-1)
    }
  }

  useEffect(() => {
    if (open) {
      setFocusedIndex(filteredOptions.length ? 0 : -1)
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }, [open, filteredOptions.length])

  useEffect(() => {
    if (!portalMenu || !open) return undefined
    const place = () => {
      const box = dropdownRef.current?.getBoundingClientRect()
      if (box) setMenuRect({ top: box.bottom + 8, left: box.left, width: box.width })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [portalMenu, open])

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as
        | HTMLElement
        | undefined
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  const renderMenu = (menu: React.ReactNode) =>
    portalMenu ? createPortal(menu, document.body) : menu

  return (
    <div
      ref={dropdownRef}
      className={clx('tw-relative tw-w-full', className)}
      tabIndex={0}
      role="combobox"
      aria-expanded={open}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        onClick={toggle}
        className={clx(
          'tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-px-2 tw-text-left tw-transition-all focus:tw-ring-2 focus:tw-ring-focus-100 focus:tw-ring-offset-2',
          compact ? 'tw-h-8 tw-py-1 tw-text-sm' : 'tw-h-12 tw-py-3',
          !open && 'hover:tw-opacity-80',
          triggerClassName,
        )}
      >
        <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 tw-font-medium">
          {Icon && (
            <span className="tw-flex-shrink-0 tw-p-0.5">
              <Icon
                className={clx(
                  'tw-h-4 tw-w-4',
                  hasSelection ? 'tw-text-content-base' : 'tw-text-neutral',
                )}
              />
            </span>
          )}

          {!collapsed && (
            <span
              className={clx(
                'tw-min-w-0 tw-flex-1 tw-truncate tw-whitespace-nowrap',
                hasSelection ? 'tw-text-content-base' : 'tw-text-neutral',
              )}
            >
              {triggerLabel}
            </span>
          )}
        </span>

        {!hideArrow && !collapsed ? (
          <ChevronDownIcon
            className={clx(
              'tw-hidden tw-h-5 tw-w-5 tw-flex-shrink-0 tw-transition-transform md:tw-flex',
              open && 'tw-rotate-180',
              hasSelection ? 'tw-text-content-base' : 'tw-text-neutral',
            )}
          />
        ) : null}
      </button>

      {open && renderMenu(
        <div
          ref={menuRef}
          className={clx(
            // Inline: z-[200] jak reszta menu w aplikacji - przy `tw-z-20` lista
            // wchodziła POD sąsiednie pola z `tw-z-50` (np. `NumberField`).
            // W portalu trzeba przebić modal (bootstrapowe `.modal` to 1050).
            'tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-shadow-multi',
            portalMenu
              ? 'tw-fixed tw-z-[9999]'
              : 'tw-absolute tw-z-[200] tw-mt-2 tw-w-full',
            compact ? 'tw-min-w-[200px]' : 'tw-min-w-[340px]',
          )}
          style={
            portalMenu && menuRect
              ? {
                  top: menuRect.top,
                  left: menuRect.left,
                  width: menuRect.width,
                }
              : undefined
          }
        >
          {!collapsed && (searchable ?? !compact) && (
            <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-p-2">
              <div className="tw-relative">
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="tw-h-10 tw-w-full tw-rounded-md tw-border tw-px-2 tw-pr-8 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-focus-100"
                />

                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('')
                      searchRef.current?.focus()
                    }}
                    className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-rounded hover:tw-bg-base-300"
                  >
                    <FaIcon
                      icon={faClose}
                      className="tw-h-3 tw-w-3 tw-opacity-70"
                    />
                  </button>
                )}
              </div>
            </div>
          )}

          {multiple && showSelectAll && filteredOptions.length > 0 && (
            <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-px-2 tw-py-1.5">
              <button
                type="button"
                onClick={toggleAll}
                className="tw-text-sm tw-font-medium tw-text-primary hover:tw-underline"
              >
                {filteredOptions.every((o) => selectedValues.includes(o.value))
                  ? 'Odznacz wszystkie'
                  : 'Zaznacz wszystkie'}
              </button>
            </div>
          )}

          <div className="custom-scrollbar hide-scrollbar tw-max-h-[300px] tw-overflow-y-auto tw-p-1.5">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-2 tw-text-sm tw-opacity-70">
                Brak wyników
              </div>
            ) : (
              <ul ref={listRef} className="tw-flex tw-flex-col tw-gap-0.5">
                {filteredOptions.map((option, idx) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => select(option)}
                      title={option.title}
                      className={clx(
                        'tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-px-1 tw-py-2 tw-text-left hover:tw-bg-base-300',
                        isSelected(option) && 'tw-bg-secondary tw-font-medium',
                        focusedIndex === idx && 'tw-bg-base-300',
                        option.disabled &&
                          !isSelected(option) &&
                          'tw-cursor-not-allowed tw-bg-error/20 hover:tw-bg-error/20',
                      )}
                    >
                      <span className="tw-flex tw-items-center tw-gap-1">
                        {option.icon && <option.icon className="tw-h-3" />}
                        {option.content || option.label}
                      </span>

                      {isSelected(option) && (
                        <CheckIcon className="tw-h-4 tw-w-4 tw-text-focus-100" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
      )}

      {name && (
        <input
          type="hidden"
          name={name}
          value={multiple ? selectedValues.join(',') : ((value as string) ?? '')}
        />
      )}
    </div>
  )
}
